import { Link, useSearch } from '@tanstack/react-router'
import { ArrowLeft, MessagesSquare, PlugZap } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, describeApiError } from '@/api/problemDetails'
import { Alert, Button, buttonVariants, EmptyState, Skeleton, Spinner } from '@/components/ui'
import { PausedForCreditsBadge } from '@/features/billing/components/PausedForCredits'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { cn } from '@/lib/utils'

import { AgentModeBadge } from './AgentModeBadge'
import { ConversationList } from './ConversationList'
import { ConversationView } from './ConversationView'
import { useConversation, useConversations } from './queries'
import type { DeskConversationFilter } from './types'

function isChannelNotReady(error: unknown): boolean {
  return error instanceof ApiError && error.problem.errorCode === 'desk_channel_not_ready'
}

/** Estado "canal ainda nao pronto" (409) com a proxima acao concreta: conectar o canal (empty state = action state). */
function ChannelNotReadyState({ tenantSlug }: { readonly tenantSlug: string }) {
  const { t } = useTranslation()

  return (
    <EmptyState
      icon={PlugZap}
      title={t('desk.channelNotReady.title')}
      description={t('desk.channelNotReady.description')}
      action={
        <Link
          to="/app/$tenantSlug/channels"
          params={{ tenantSlug }}
          className={buttonVariants({ variant: 'primary', size: 'sm' })}
        >
          {t('desk.channelNotReady.action')}
        </Link>
      }
    />
  )
}

/**
 * Desk MVP (WP-15): o operador humano lista as conversas do seu tenant, abre uma (mensagens + sugestoes do agente) e
 * responde publicamente. O conversation engine permanece headless — nada aqui cita o provedor de canal/infra (docs/design/00 s5).
 * O modo do agente fica sempre visivel (docs/design/01 s11); estados vazios trazem a proxima acao (s7).
 */
export function DeskPage() {
  const { t, i18n } = useTranslation()
  const { tenant } = useCurrentTenant()
  // Pendências abre uma conversa específica (?conversation=<seq>); sem o parâmetro, nada selecionado.
  const search = useSearch({ strict: false })
  const [filter, setFilter] = useState<DeskConversationFilter>(
    search.conversation === undefined ? 'open' : 'all',
  )
  const [selectedId, setSelectedId] = useState<number | null>(search.conversation ?? null)

  const conversationsQuery = useConversations(tenant?.id, filter)
  const conversationQuery = useConversation(tenant?.id, selectedId)

  if (tenant === null) {
    return null
  }

  const agentMode = conversationsQuery.data?.agentMode ?? null

  return (
    <div className="flex h-[calc(100dvh-8rem)] min-h-[480px] flex-col gap-3">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-foreground-primary">{t('desk.title')}</h1>
        {/* Modo do agente sempre visivel; enquanto nao resolvido, mostra placeholder (nao afirma "sem agente"). */}
        <div className="flex items-center gap-2">
          <PausedForCreditsBadge tenantId={tenant.id} />
          <AgentModeBadge mode={agentMode} loading={!conversationsQuery.isSuccess} />
        </div>
      </div>

      {isChannelNotReady(conversationsQuery.error) ? (
        <ChannelNotReadyState tenantSlug={tenant.slug} />
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 overflow-hidden md:grid-cols-[320px_1fr]">
          {/* Coluna 1 — lista (no mobile some quando ha conversa aberta). */}
          <section
            className={cn(
              'min-h-0 overflow-hidden rounded-lg border border-border-default bg-background-default',
              selectedId !== null && 'hidden md:block',
            )}
          >
            {conversationsQuery.isPending ? (
              <div
                className="flex flex-col gap-px"
                role="status"
                aria-busy="true"
                aria-label={t('loading')}
              >
                {Array.from({ length: 7 }, (_, index) => (
                  <div key={index} className="flex items-center gap-3 px-4 py-3">
                    <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Skeleton className="h-3.5 w-1/2" />
                      <Skeleton className="h-3 w-4/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : conversationsQuery.isError ? (
              <div className="p-4">
                <Alert variant="danger" title={t('desk.list.loadFailed')}>
                  <ul className="list-disc pl-4">
                    {describeApiError(conversationsQuery.error).map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                  <div className="mt-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        void conversationsQuery.refetch()
                      }}
                    >
                      {t('retry')}
                    </Button>
                  </div>
                </Alert>
              </div>
            ) : (
              <ConversationList
                conversations={conversationsQuery.data.conversations}
                selectedId={selectedId}
                onSelect={setSelectedId}
                filter={filter}
                onFilterChange={setFilter}
                onReload={() => {
                  void conversationsQuery.refetch()
                }}
                language={i18n.language}
              />
            )}
          </section>

          {/* Coluna 2 — conversa aberta (no mobile some quando nenhuma esta aberta). */}
          <section
            className={cn(
              'min-h-0 overflow-hidden rounded-lg border border-border-default bg-background-default',
              selectedId === null && 'hidden md:block',
            )}
          >
            {selectedId === null ? (
              <div className="flex h-full items-center justify-center p-6">
                <EmptyState
                  icon={MessagesSquare}
                  title={t('desk.detail.noneSelectedTitle')}
                  description={t('desk.detail.noneSelectedDescription')}
                />
              </div>
            ) : (
              <div className="flex h-full flex-col">
                {/* Voltar para a lista (so no mobile). */}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(null)
                  }}
                  className="flex shrink-0 items-center gap-1.5 border-b border-border-default p-2 text-sm text-foreground-secondary hover:text-foreground-primary md:hidden"
                >
                  <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                  {t('desk.detail.back')}
                </button>

                {conversationQuery.isPending ? (
                  <div className="flex flex-1 items-center justify-center p-6">
                    <Spinner label={t('loading')} />
                  </div>
                ) : isChannelNotReady(conversationQuery.error) ? (
                  <div className="flex flex-1 items-center justify-center p-6">
                    <ChannelNotReadyState tenantSlug={tenant.slug} />
                  </div>
                ) : conversationQuery.isError ? (
                  <div className="p-4">
                    <Alert variant="danger" title={t('desk.detail.loadFailed')}>
                      <ul className="list-disc pl-4">
                        {describeApiError(conversationQuery.error).map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                      <div className="mt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            void conversationQuery.refetch()
                          }}
                        >
                          {t('retry')}
                        </Button>
                      </div>
                    </Alert>
                  </div>
                ) : (
                  <ConversationView
                    key={conversationQuery.data.id}
                    tenantId={tenant.id}
                    detail={conversationQuery.data}
                    language={i18n.language}
                  />
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
